import { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import ReactSelect, { components } from "react-select";
import expandArrowImg from "../../../assets/img/inputs/keyboard_arrow_down.svg";
import { Text } from "../Text";

const SelectContainer = styled.div(({ helperText, theme, disabled }) => ({
  position: "relative",
  flex: 1,
  fontFamily: "NotoSansHebrew",
  fontSize: 16,
  fontWeight: 300,
  borderRadius: 4,
  border: `1px solid ${helperText ? theme.color.error : "#D4D4D4"}`,
  pointerEvents: disabled ? "none" : "auto",
  borderColor: "rgba(0, 0, 0, 0.23)",
}));

const StyledSelect = styled(ReactSelect)(() => ({
  fontFamily: "NotoSansHebrew",
  fontSize: 16,
  fontWeight: 300,
  width: "fit-content",
}));

const Error = styled.div(({ theme }) => ({
  fontSize: 12,
  color: theme.color.error,
  marginTop: "0.5em",
}));

const Label = styled(Text)(({ shrink }) => ({
  display: shrink ? "block" : "none",
  fontWeight: 400,
  fontSize: "13px !important",
  padding: "6px",
  position: "absolute",
  right: "12px",
  top: "-14px",
  backgroundColor: "#ffffff",
  zIndex: 999,
  color: "#8C89AC",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StyleStartImg = styled.img(() => ({
  width: 20,
  height: 20,
  alignSelf: "center",
}));

const SelectMobile = ({
  label,
  value,
  error,
  name,
  options = [],
  onChange,
  width,
  height,
  helperText,
  startImg,
  placeholder,
  clearable = true,
  disabled,
  ...props
}) => {
  const theme = useTheme();

  const isValidValue =
    value &&
    typeof value === "object" &&
    (typeof value.value === "string" || typeof value.value === "number");

  const [shrink, setShrink] = useState(() => !!(isValidValue && (value.label || value.value)));

  const normalizedValue = isValidValue ? value : null;

  useEffect(() => {
    setShrink(!!normalizedValue);
  }, [normalizedValue]);

  // Auto-select the only available option
  useEffect(() => {
    if (options?.length === 1 && !normalizedValue) {
      onChange?.(options[0]);
    }
  }, [options, normalizedValue, onChange]);

  const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      <img src={expandArrowImg} alt="dropdown arrow" width={18} height={18} />
    </components.DropdownIndicator>
  );

  const Control = ({ children, ...controlProps }) => (
    <components.Control {...controlProps}>
      {!!startImg && <StyleStartImg src={startImg} alt="icon" />}
      {children}
    </components.Control>
  );

  return (
    <SelectContainer style={props.style} disabled={disabled} helperText={helperText}>
      {label && <Label shrink={shrink.toString()}>{label}</Label>}
      <StyledSelect
        aria-label={label}
        name={name}
        value={normalizedValue}
        onChange={onChange}
        options={options}
        isClearable={clearable}
        isDisabled={disabled}
        placeholder={placeholder}
        closeMenuOnSelect
        components={{ DropdownIndicator, Control }}
        styles={{
          singleValue: (base) => ({
            ...base,
            width: "fit-content",
          }),
          container: (base) => ({
            ...base,
            backgroundColor: theme.color.white,
            borderRadius: 6,
            width: width || "100%",
          }),
          control: (base) => ({
            ...base,
            backgroundColor: theme.color.white,
            boxShadow: "none",
            padding: "0 9px",
            border: 0,
            borderRadius: 10,
            height: height || "3em",
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: 0,
          }),
          input: (base) => ({
            ...base,
            textAlign: "start",
            color: theme.color.text,
            fontSize: 16,
            padding: 0,
          }),
          indicatorSeparator: () => ({ display: "none" }),
          menuList: (base) => ({
            ...base,
            marginRight: 5,
          }),
          placeholder: (base) => ({
            ...base,
            textAlign: "start",
            color: "#BDBFCE",
            fontSize: 16,
          }),
        }}
      />
      {helperText && <Error>{helperText}</Error>}
    </SelectContainer>
  );
};

export default SelectMobile;
